import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { DaypartService } from '../../../../woo_services.module/DaypartService';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import {
  Daypart,
  PerIdEstimates,
  ProductFormat,
  Publisher,
  SlotType,
  wooId,
} from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'manage-dayparts',
  templateUrl: './manage-dayparts.component.html',
  styleUrls: ['../../shared//table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageDayparts implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  show = {
    withdrawalLimitsHaveChanged: false,
    updatingEstimates: false,
    loading: true,
    isEditing: false,
  };
  readonly staticFields = 4;

  readonly tableHeaders = ['Publicist', 'Placering', 'Daypart', 'Tidsspann', 'Preroll', 'Midroll', 'Postroll'];
  withdrawalLimitsHaveChanged = false;
  daypartsRows: DaypartsRow[];
  publishers: Publisher[] = [];
  selectedPublisher: Publisher;
  productFormats: ProductFormat[];
  selectedProductFormat: ProductFormat;
  includedProductFormatSlotTypes: Record<wooId, Record<SlotType, boolean>> = {};
  dayparts: Daypart[];
  selectedDaypart: Daypart;
  translateProductFormat: (args: any) => string;
  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private daypartService: DaypartService,
    private modalService: ModalService,
    private utilityService: ProductsPageUtilityService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.translateProductFormat = this.translationService.convertProductFormat;
    this.daypartService
      .getDayparts()
      .then((dayparts) => (this.dayparts = dayparts))
      .then(() => this.publisherService.getPublishers(ExtraPublisherField.enabled))
      .then((publishers) => {
        this.publishers = publishers;
        this.publishers = publishers.filter((p) => p.enabled);
      })
      .then(() => {
        return this.getAndBuildDayParts();
      })
      .finally(() => {
        this.show.loading = false;
      });
  }

  async getAndBuildDayParts(): Promise<void> {
    return this.productFormatService
      .getProductFormats(
        ExtraProductFormatField.enabled,
        ExtraProductFormatField.daypartWithdrawalLimits,
        ExtraProductFormatField.slotTypes,
      )
      .then((productFormats) => {
        this.productFormats = productFormats.filter((f) => f.enabled);
        this.includedProductFormatSlotTypes = this.utilityService.setIncludedProductFormatSlotTypes(
          this.productFormats,
        );
        this.buildDaypartRows();
        this.changeDetection.markForCheck();
      });
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  cancelEditing(): void {
    this.getAndBuildDayParts();
    this.setEditing(false);
  }

  async save(): Promise<void> {
    const filterActive = this.selectedDaypart || this.selectedPublisher || this.selectedProductFormat;
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then((status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          this.show.updatingEstimates = true;
          this.productFormatService
            .updateProductFormats(this.productFormats)
            .then((productFormats) => {
              this.productFormats = productFormats;
              this.show.withdrawalLimitsHaveChanged = true;
              this.buildDaypartRows();
            })
            .finally(() => {
              loaderModal.close();
              this.setEditing(false);
              this.show.updatingEstimates = false;
              this.changeDetection.markForCheck();
            });
        }
      });
  }

  rowVisible(obj: DaypartsRow): boolean {
    const publisherFiltered = this.selectedPublisher && obj.publisherId !== this.selectedPublisher.id;
    const daypartFiltered = this.selectedDaypart && obj.daypartId !== this.selectedDaypart.id;
    const productFormatFiltered = this.selectedProductFormat && obj.productFormatId !== this.selectedProductFormat.id;
    return !(publisherFiltered || daypartFiltered || productFormatFiltered);
  }

  getDaypartName(id: wooId): string {
    return this.dayparts.find((device) => device.id === id).name;
  }

  getDaypartDescription(id: wooId): string {
    return this.dayparts.find((device) => device.id === id).description;
  }

  getDaypartWithdrawalLimits(productFormatId: wooId): PerIdEstimates {
    return this.productFormats.find((format) => format.id === productFormatId).daypart_withdrawal_limits;
  }

  private buildDaypartRows(): void {
    let daypartsRows: DaypartsRow[] = [];
    this.publishers.map((publisher) => {
      this.utilityService.getPublisherSpecificProductFormats(publisher, this.productFormats).map((productFormat) => {
        Object.keys(productFormat.daypart_withdrawal_limits).map((daypartId) => {
          daypartsRows = [
            ...daypartsRows,
            {
              publisher: publisher.name,
              publisherId: publisher.id,
              productFormat: productFormat.name,
              productFormatId: productFormat.id,
              daypart: this.getDaypartName(daypartId),
              daypartDescription: this.getDaypartDescription(daypartId),
              daypartId: daypartId,
              withdrawalLimitsPreroll: productFormat.daypart_withdrawal_limits[daypartId].preroll,
              withdrawalLimitsMidroll: productFormat.daypart_withdrawal_limits[daypartId].midroll,
              withdrawalLimitsPostroll: productFormat.daypart_withdrawal_limits[daypartId].postroll,
            },
          ];
        });
      });
    });
    this.daypartsRows = daypartsRows;
  }
}

interface DaypartsRow {
  publisher: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId: wooId;
  daypart: string;
  daypartDescription: string;
  daypartId: wooId;
  withdrawalLimitsPreroll?: number;
  withdrawalLimitsMidroll?: number;
  withdrawalLimitsPostroll?: number;
}
